<template>
  <div class="main">
    <FormWrapper :action="route('course-request.store')" method="POST">
      <div class="title-bar">
        <div class="row">
          <div class="col-12 col-md-6 mb-4 text-center text-md-left">
            <h1 class="mb-0">Request a Course</h1>
          </div>
          <div class="col-12 col-md-6 mb-3 text-center text-md-right">
            <button
              type="submit"
              class="d-none d-lg-inline-block btn btn-success mr-2"
            >
              Submit Request
            </button>
            <a class="btn btn-danger" :href="cancelUrl">Cancel & Exit</a>
          </div>
        </div>
      </div>

      <div class="content py-4 px-xl-2">
        <div class="container">
          <div class="row">
            <div class="col-12">
              <transition name="fade">
                <Alert v-if="hasAlert" :alert="alert" />
              </transition>
              <div class="alert alert-info mb-5" v-if="showForm">
                This form should be used to request the addition of a new
                distributor course to My Learning Center. My Learning Center
                administration will review the request and contact you regarding
                status.
              </div>
              <div class="mt-5 text-center" v-else>
                  <a :href="route('catalog.online')" class="btn btn-primary">Return to Catalog</a>
              </div>
            </div>

            <div class="row" v-if="showForm">
                <div class="col-12 col-lg-8 mb-4">
                <div class="row">
                    <div class="col-12 mb-3">
                    <Field
                        type="text"
                        name="name"
                        label="Course Name"
                        validate="required"
                        :value="course.name"
                    />
                    </div>
                    <div class="col-12 col-md-6 col-xl-8 mb-3">
                    <Field
                        type="text"
                        name="length"
                        label="Course Length"
                        help-text="A descriptive statement about the length of the course"
                        :value="course.length"
                    />
                    </div>
                    <div class="col-12 col-md-6 col-xl-4 mb-3">
                    <Field
                        type="text"
                        name="duration"
                        label="Duration (in hours)"
                        validate="required"
                        :value="course.duration"
                    />
                    </div>
                    <div class="col-12 col-md-6 col-xl-4 mb-3">
                    <Field
                        type="text"
                        name="cost"
                        label="Standard Cost"
                        :value="course.cost"
                    />
                    </div>
                    <div class="col-12 col-md-6 col-xl-4 mb-3">
                    <Field
                        type="text"
                        name="fad_cost"
                        label="FAD/Elite Cost"
                        :value="course.fad_cost"
                    />
                    </div>
                    <div class="d-none d-xl-block col-xl-4">
                    <!-- spacer -->
                    </div>
                    <div class="col-12 col-md-6 col-xl-4 mb-3">
                    <Field
                        type="text"
                        name="min_attendees"
                        label="Minimum Attendees"
                        help-text="Leave blank for no minimum restriction"
                        :value="course.min_attendees"
                    />
                    </div>
                    <div class="col-12 col-md-6 col-xl-4 mb-3">
                    <Field
                        type="text"
                        name="max_attendees"
                        label="Maximum Attendees"
                        help-text="Leave blank for no maximum restriction"
                        :value="course.max_attendees"
                    />
                    </div>
                    <div class="d-none d-xl-block col-xl-4">
                    <!-- spacer -->
                    </div>
                    <div class="col-12 col-md-6 col-xl-4 mb-3">
                    <Field
                        type="text"
                        name="nate_id"
                        label="NATE Course ID (if applicable)"
                        help-text="NATE ID will ONLY appear on student certificates for associated events"
                        :value="course.nate_id"
                    />
                    </div>
                    <div class="col-12 col-md-6 col-xl-4 mb-3">
                    <Field
                        type="text"
                        name="hours"
                        label="FAD\Elite Distributor Hours"
                        help-text="Number of hours awarded for completing the course"
                        :value="course.hours"
                    />
                    </div>
                    <div class="d-none d-xl-block col-xl-4">
                    <!-- spacer -->
                    </div>
                </div>
                </div>

                <div class="col-12 col-lg-4 mb-4">
                <div class="row">
                    <div class="col-12 col-md-6 col-lg-12">
                    <MultiAssignField
                        type="select"
                        label="Brand"
                        name="brands"
                        :options="brands"
                        :value="course.course_brands"
                        :disabled="brands.length == 1"
                    />
                    </div>
                    <div class="col-12 col-md-6 col-lg-12">
                    <MultiAssignField
                        type="select"
                        label="Topic"
                        name="topics"
                        :options="topics"
                        :value="course.course_topics"
                    />
                    </div>
                </div>
                </div>

                <div class="col-12 mb-4">
                <h4 class="text-uppercase">Course Description</h4>
                <p class="font-size-sm">
                    The information entered here will appear on the course details
                    page.
                </p>
                <HtmlField
                    name="description"
                    :value="course.description"
                    :hide-asset-icons="true"
                />
                </div>

                <div class="col-12 mb-4">
                <h4 class="text-uppercase">Admin Notes</h4>
                <p class="font-size-sm">
                    The information entered here will only be seen by My Learning
                    Center administration.
                </p>
                <HtmlField
                    name="admin_notes"
                    :value="course.admin_notes"
                    :hide-asset-icons="true"
                />
                </div>

                <div class="col-12 mb-4">
                <h4 class="text-uppercase">Course Details (File Upload)</h4>
                <p class="font-size-sm">
                    Course details must be supplied prior to approval. This can be a
                    flyer, agenda, or outline. PDF or MS Word format only. Maximum
                    file size allowed: 8 MB.
                </p>
                <Uploader
                    id="course-flyer-upload"
                    :upload-url="route('course-request.flyer')"
                    :hide-instructions="true"
                    :hide-max-upload="true"
                    @uploaded="flyerUploaded"
                    class="mb-3"
                />
                <input type="hidden" name="course_flyer" :value="courseFlyer" />
                <div
                    v-if="!!courseFlyer"
                    class="alert alert-success font-weight-bold"
                >
                    {{ courseFlyer }}
                </div>
                </div>

                <div class="col-12 mb-5">
                <hr />
                <h3 class="text-uppercase mb-3">Distributor Course Definition</h3>
                <p class="mb-4">
                    Distributor Courses are courses presented by distribution
                    instructors or any instructor that is not a current My Learning
                    Center Training Partner. Courses may be any subject matter which
                    benefit a dealer, but must not be linked to competitive
                    products. Technical courses provided by the Carrier
                    University-Syracuse training team qualify within the Distributor
                    training category. Recruitment and retention events, product
                    webinars, and presentations at Dealer Meetings do not earn Elite
                    Dealer/FAD training credit. Each occurrence of a distributor
                    course will incur a $25 administration fee.
                </p>
                <Field
                    type="checkbox"
                    name="agree_to_terms"
                    id="cb_agree_to_terms"
                    label="I understand the requirements of a distributor course."
                    :value="!!course.agree_to_terms"
                    :checkbox-value="1"
                />
                </div>

                <div class="col-12 text-center text-lg-left">
                <button
                    type="submit"
                    class="btn btn-success w-75 mb-3 w-sm-auto mr-sm-1 mb-sm-0"
                >
                    Submit Request
                </button>
                <a class="btn btn-danger w-75 w-sm-auto ml-sm-1" :href="cancelUrl"
                    >Cancel & Exit</a
                >
                </div>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
import Uploader from "components/pages/admin/assets/partials/Uploader";

export default {
  mixins: [DisplaysAlerts],
  components: { Uploader },
  props: {
    course: { type: Object, required: true },
    brands: { type: Array, required: true },
    topics: { type: Array, required: true },
    referer: { type: String, default: null },
    successMessage: { }
  },
  data() {
    return {
      courseFlyer: null,
      showInstructions: false,
      showForm: true
    };
  },
  computed: {
    cancelUrl() {
      return this.referer || this.route("catalog.classroom");
    },
  },
  methods: {
    flyerUploaded(file, response) {
      this.courseFlyer = response.file;
      $("#course-flyer-upload .alert").remove();
    },
  },
  created() {
    if (this.successMessage) {
        this.showForm = false;
    }
    this.courseFlyer = !!this.course.course_flyer
      ? this.course.course_flyer
      : null;
  },
};
</script>

<style scoped>
p {
  color: #666666;
}
</style>